import React, { memo, useEffect, useState } from 'react'
import clsx from 'clsx'

import { IInputFactoryProps, IInputWrapperWithLabelProps } from './InputFactoryProps'
import { IncomeAdditionalFieldsNames, IncomeBasicFieldsNames } from '../../../formDefinitions/IncomeInfoForm/IncomeInfoFields'
import { IDropdownBooleanOption, IDropdownChangeIncomeFieldsOption, IDropdownOption } from './InputDropdown/InputDropdownProps'
import InputText from './InputText/InputText'
import InputDropdown from './InputDropdown/InputDropdown'
import InputNumber from './InputNumber/InputNumber'
import InputDate from './InputDate/InputDate'
import useInputFactoryLogic from './hooks/useInputFactoryLogic'
import { Environment } from '../../../environment/app-settings'
import { AdditionalIncomeAdditionalFieldsNames } from '../../../formDefinitions/IncomeInfoForm/AdditionalIncomeFields'
import { FieldTypes, HtmlInputTypes } from '../../../models/form/IFieldsDefinition'


import './InputBaseStyles.scss'


const InputWrapperWithLabel = memo((props: IInputWrapperWithLabelProps) => {
   let labelFontSize = 18
   const DESKTOP_SCREEN_BREAKPOINT = 840
   const [currentWindowWidth, setCurrentWindowWidth] = useState(window.innerWidth)

   const onWindowResize = () => {
      setCurrentWindowWidth(window.innerWidth)
   }

   const TOO_LONG_LABELS: string[] = [
      IncomeBasicFieldsNames.CostOfLiving,

      IncomeAdditionalFieldsNames.IsEmploymentIndefinitely, 
      IncomeAdditionalFieldsNames.IsBenefitIndefinitely,
      IncomeAdditionalFieldsNames.HasUnionGrants,
      IncomeAdditionalFieldsNames.BenefitRecivedFromDate,
      IncomeAdditionalFieldsNames.FarmRunningFromDate,

      AdditionalIncomeAdditionalFieldsNames.AdditionalIncomeIsEmploymentIndefinitely, 
      AdditionalIncomeAdditionalFieldsNames.AdditionalIncomeIsBenefitIndefinitely,
      AdditionalIncomeAdditionalFieldsNames.AdditionalIncomeHasUnionGrants,
      AdditionalIncomeAdditionalFieldsNames.AdditionalIncomeBenefitRecivedFromDate,
      AdditionalIncomeAdditionalFieldsNames.AdditionalIncomeFarmRunningFromDate,
   ]

   if(TOO_LONG_LABELS.includes(props.name) && currentWindowWidth < DESKTOP_SCREEN_BREAKPOINT){
      labelFontSize = 15 //Jeśli labelka nie mieści się w polu PRZY WIDOKU MOBILNYM zmniejszamy jej rozmiar, dla ustalonych z góry kontrolek.
   }

 
   useEffect(() => {
      window.addEventListener('resize', onWindowResize) //Nasłuchujemy na zmianę szerokości ekranu
         
      return () => {
         window.removeEventListener('resize', onWindowResize) //Czyszczenie przy odmontowaniu komponentu
      }
   }, [])


   return (
      <div className={clsx(Environment.fieldBaseClassName, props.error && Environment.fieldErrorClassName )}>
         {props.children}
         <label 
            htmlFor={props.name as string} 
            className={clsx((props.value || props.hasInputFocused) && 'form-input-base-filled')}
            style={{ fontSize: labelFontSize }}
         >
            {props.label}
         </label>
         { props.error && <p id={`${props.name}-helper-text`} className='form-input-base-error-helper-text'>{props.error}</p> }
      </div>
   )
})



const InputFactory = memo((props: IInputFactoryProps) => {      
   const [hasInputFocused, setHasInputFocused] = useState<boolean>(false)
   const { fieldDefinition: field, fieldState } = props

   const fieldValue = fieldState.fieldState !== undefined ? fieldState.fieldState : ''
   const fieldValidationError = fieldState.validationError || undefined   

   
   const {
      handleOnBlur,
      handlePaste,
      handleSaveInputValueToStateAndValid,
      handleOnBooleanChange,
      handleOnDropdownChange,
      handleOnDropdownChangeIncomeFieldsChange,

      debounceTime,
   } = useInputFactoryLogic(props)


   switch(field.type){
      case FieldTypes.string: {
         return (
            <InputWrapperWithLabel 
               error={fieldValidationError} 
               label={field.label} 
               name={field.name} 
               value={fieldValue} 
               hasInputFocused={hasInputFocused}
            >
               <InputText
                  value={fieldValue as string}
                  field={field}
                  debounceTime={debounceTime}
                  htmlInputType={HtmlInputTypes.text}
                  setHasInputFocused={setHasInputFocused}
                  handleOnBlur={handleOnBlur}
                  handleSaveInputValueToStateAndValid={handleSaveInputValueToStateAndValid}
               />
            </InputWrapperWithLabel>
         )
      }
      case FieldTypes.email: {        
         const handleOnEmailFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            const allowedChars = /[0-9a-zA-Z-._@]/
                     
            if(!allowedChars.test(e.key)){
               e.preventDefault();
            }
         }

         return (
            <InputWrapperWithLabel 
               error={fieldValidationError} 
               label={field.label} 
               name={field.name} 
               value={fieldValue} 
               hasInputFocused={hasInputFocused} 
            >
               <InputText 
                  value={fieldValue as string}
                  htmlInputType={HtmlInputTypes.email}
                  field={field}
                  debounceTime={debounceTime}
                  setHasInputFocused={setHasInputFocused}
                  handleOnBlur={handleOnBlur}
                  handleOnPaste={handlePaste}
                  handleOnKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleOnEmailFieldKeyDown(e)}
                  handleSaveInputValueToStateAndValid={handleSaveInputValueToStateAndValid}
               />
            </InputWrapperWithLabel>
         )
      }
      case FieldTypes.phone: {
         return (
            <InputWrapperWithLabel 
               error={fieldValidationError} 
               label={field.label} 
               name={field.name} 
               value={fieldValue} 
               hasInputFocused={hasInputFocused} 
            >
               <InputText 
                  value={fieldValue as string}
                  field={field}
                  debounceTime={debounceTime}
                  htmlInputType={HtmlInputTypes.tel}
                  setHasInputFocused={setHasInputFocused}
                  handleOnBlur={handleOnBlur}
                  handleSaveInputValueToStateAndValid={handleSaveInputValueToStateAndValid}
               />
            </InputWrapperWithLabel>
         )
      }
      case FieldTypes.boolean: {
         return (
            <InputWrapperWithLabel 
               error={fieldValidationError} 
               label={field.label} 
               name={field.name} 
               value={fieldValue} 
               hasInputFocused={hasInputFocused} 
            >
               <InputDropdown
                  name={field.name}
                  type={FieldTypes.boolean}
                  editable={!field.editable} 
                  inputLabel={field.label}
                  htmlInputType={HtmlInputTypes.text}
                  handleOnChange={handleOnBooleanChange}
                  value={fieldValue as IDropdownBooleanOption}
                  options={field.options}
               />
            </InputWrapperWithLabel>
         )
      }
      case FieldTypes.number: {
         return (
            <InputWrapperWithLabel 
               error={fieldValidationError} 
               label={field.label} 
               name={field.name} 
               value={fieldValue} 
               hasInputFocused={hasInputFocused} 
            >
               <InputNumber
                  value={fieldValue as number}
                  field={field}
                  debounceTime={debounceTime}
                  htmlInputType={HtmlInputTypes.text}
                  setHasInputFocused={setHasInputFocused}
                  handleOnBlur={handleOnBlur}
                  handleSaveInputValueToStateAndValid={handleSaveInputValueToStateAndValid}
               />
            </InputWrapperWithLabel>
         )
      }
      case FieldTypes.dropdown: {
         return (
            <InputWrapperWithLabel 
               error={fieldValidationError} 
               label={field.label} 
               name={field.name} 
               value={(fieldValue as IDropdownOption).value} 
               hasInputFocused={hasInputFocused} 
            >
               <InputDropdown 
                  name={field.name}
                  type={field.type} 
                  options={field.options}
                  inputLabel={field.label}
                  value={fieldValue as IDropdownOption}
                  editable={!field.editable} 
                  htmlInputType={HtmlInputTypes.text}
                  handleOnChange={handleOnDropdownChange}
               />
            </InputWrapperWithLabel>
         )
      }
      case FieldTypes.dropdownChangeIncomeTypeField: {
         return (
            <InputWrapperWithLabel 
               error={fieldValidationError} 
               label={field.label} 
               name={field.name} 
               value={(fieldValue as IDropdownChangeIncomeFieldsOption).value} 
               hasInputFocused={hasInputFocused}  
            >
               <InputDropdown 
                  name={field.name}
                  type={field.type} 
                  options={field.options}
                  inputLabel={field.label}
                  value={fieldValue as IDropdownChangeIncomeFieldsOption}
                  editable={!field.editable} 
                  htmlInputType={HtmlInputTypes.text}
                  handleOnChange={handleOnDropdownChangeIncomeFieldsChange}
               />
            </InputWrapperWithLabel>
         )
      }
      case FieldTypes.date: {    
         return (
            <InputWrapperWithLabel 
               error={fieldValidationError} 
               label={field.label} 
               name={field.name} 
               value={fieldValue} 
               hasInputFocused={hasInputFocused}
            >
               <InputDate
                  value={fieldValue as string ?? null}
                  field={field}
                  debounceTime={debounceTime}
                  htmlInputType={HtmlInputTypes.text}
                  setHasInputFocused={setHasInputFocused}
                  handleOnBlur={handleOnBlur}
                  handleSaveInputValueToStateAndValid={handleSaveInputValueToStateAndValid}
               />
            </InputWrapperWithLabel>
         )
      }

      default: {
         return <div>Not implemented</div>
      }
   }

})

export default InputFactory